/* ==========================================================================
   hero-wordmark-collapse.js
   --------------------------------------------------------------------------
   On the homepage's video-hero, the ROYALS wordmark starts big and low in
   the frame (assets/section-video-hero.css section 5, assets/client-
   overrides.css section 11 — the crop that makes that possible). Scrolling
   down shrinks that same wordmark and moves it up into the header's
   "ROYALS" link (.overlay-nav__home, snippets/header-overlay-nav.liquid),
   which shows no text of its own any more (component-overlay-nav.css —
   "NO VISIBLE TEXT, EVER") — the svg is the only thing that ever occupies
   that slot. Scrolling back to the top reverses it exactly.

   THE APPROACH — a live FLIP, not a triggered one-shot animation

   A cloned copy of .video-hero__mark-crop (the crop wrapper AND its inner
   image — see WHY THE CLONE IS THE CROP WRAPPER, NOT THE IMG below) is
   inserted as a sibling of .overlay-nav__home, frozen at the hero
   wordmark's own measured size, then permanently `position: fixed`. Its
   `transform` is a pure function of one number — scroll progress, 0 at the
   top of the page to 1 once scrolled `travel` px. There is no "collapse"
   animation and no separate "reverse" animation: scrolling up simply
   decreases the same progress value, so the interpolation runs backwards on
   its own. This is also why there is nothing to "restart" if a visitor
   scrolls up and down repeatedly mid-transition — the transform at any
   instant is a direct function of scrollY, not of which direction the last
   scroll event moved.

   The clone is opaque (`opacity: 1`) for the entire scroll, at every
   progress from 0 to 1 — it never fades, and neither does anything else,
   because there is no text underneath it to hand off to any more.

   The ORIGINAL hero wordmark is hidden (visibility, not display — see
   HIDING THE ORIGINAL below) the moment the clone is live, so the clone is
   the only copy on screen at any time; the two are pixel-identical at
   progress 0, so the swap is invisible.

   ONE INTERPOLATION, NOT TWO PHASES — `travel` IS CHOSEN SO IT ARRIVES
   EXACTLY WHEN IT FINISHES SHRINKING

   Earlier versions of this file split progress into a "shrink in place"
   phase and then a separate "slide into the header" phase, because
   `travel` (how much scroll the whole gesture takes) was capped at a fixed
   480px regardless of how far the header actually is. That produced a
   visible seam: for most of the scroll the wordmark tracked the hero
   perfectly, then at the 65% mark it visibly let go and independently
   climbed the rest of the way, because 480px of scroll was not enough
   distance for 1-for-1 hero-tracking alone to reach the header.

   The fix is not a second phase — it is to stop capping `travel` and
   instead set it to EXACTLY the vertical distance between the wordmark's
   starting point and the header's slot (see THE MATH). Once `travel` is
   that distance, "translate up by exactly scrollY" (natural, 1-for-1
   hero-tracking) and "translate up by finalDeltaY, linearly with progress"
   become the SAME formula — see WHY ONE FORMULA IS BOTH, below — so there
   is nothing left for a second phase to do, and no seam. It also means the
   whole gesture now takes a full hero-height-scale scroll instead of a
   capped 480px, which is what "slow the shrink down" turns out to mean here:
   the shrink was never a fixed-duration animation with its own speed knob,
   it just finishes exactly when the scroll-tracked translate reaches its
   target, and giving it correct, uncapped distance to do that in over is
   what makes it feel slower.

   WHY ONE FORMULA IS BOTH "TRACK THE HERO" AND "INTERPOLATE TO THE TARGET"

   Natural hero-tracking, at progress p, is translateY = -(scrollY) =
   -(p * travel) — see THE TRACK for why. Linear interpolation to the target
   is translateY = finalDeltaY * p. These are the same formula exactly when
   travel = -finalDeltaY (finalDeltaY is negative — the header is above the
   hero — so travel = |finalDeltaY|, a positive distance):

     -(p * travel) = -(p * -finalDeltaY) = p * finalDeltaY = finalDeltaY * p

   So travel is not a tuning knob any more; it is DERIVED from finalDeltaY
   in measure(), and translateY = finalDeltaY * p is simultaneously "track
   the hero" (true for every p, not just up to some cutoff) and "arrive
   exactly on target at p == 1". translateX and scale use the same plain
   linear form for the same p, so everything reaches its target at exactly
   the same instant, with no separate slide.

   THE TRACK — why -(scrollY) reproduces natural scrolling

   A position: fixed element's document-space position never changes on its
   own; only the viewport's window onto the page does. translateY(-scrollY)
   moves the element's on-screen position by exactly the amount the window
   has moved, which is indistinguishable from an ordinary, non-fixed element
   that simply never left the page flow. That is what makes this read as
   "anchored to the bottom of the hero" rather than "anchored to the
   viewport": it is not literally still in flow, but the arithmetic produces
   the same screen position at every scroll offset that being in flow would.

   THE PIVOT — why it shrinks in place rather than toward a corner

   component-hero-wordmark-fly.css sets this clone's transform-origin to
   "50% 100%" — its own bottom-centre. scale() alone never moves its pivot
   point; only the OPPOSITE end of the box moves toward or away from it. So
   at any instant, adding scale() to whatever translate() has already placed
   the element at does not additionally displace its bottom-centre point —
   the two compose without fighting each other, which is what lets scale
   and translate be driven by the same `p` with no interaction between them.

   THE MATH

     startCenterX = startRect.left + startRect.width / 2
     startBottomY = startRect.top + startRect.height
     navCenterX   = endRect.left + endRect.width / 2
     navBottomY   = endRect.top + endRect.height
     finalDeltaX  = navCenterX - startCenterX
     finalDeltaY  = navBottomY - startBottomY        (negative: the header is above the hero)
     scale        = endRect.height / startRect.height   (see WHY HEIGHT, BELOW)
     travel       = -finalDeltaY                      (see WHY ONE FORMULA IS BOTH)

   at progress p (0 at the top of the page, 1 once scrolled `travel` px):

     transform: translate(finalDeltaX * p, finalDeltaY * p) scale(1 + (scale - 1) * p)

   WHY HEIGHT, NOT WIDTH OR A SEPARATE X/Y SCALE

   .overlay-nav__home's box and the hero mark's box do not share an aspect
   ratio (one is sized by a short nav row, the other by the hero's own
   width), so no single scale reproduces both dimensions exactly — a
   non-uniform scale(sx, sy) would work the numbers but visibly squash or
   stretch the wordmark as it approached the header. Scaling uniformly by
   the HEIGHT ratio keeps it looking like itself the whole way; the
   resulting width landing close to but not exactly on .overlay-nav__home's
   own width is fine, since nothing else is visible there to compare it to
   any more.

   WHY THE CLONE IS THE CROP WRAPPER, NOT THE IMG

   .video-hero__mark-crop's own rect is what a visitor actually sees — an
   aspect-ratio box with overflow: hidden. The <img> inside it
   (.video-hero__mark) is deliberately rendered LARGER than that box and
   shifted (client-overrides.css section 11) so the crop can clip the
   artwork's own dead space. Measuring or animating the <img> directly would
   size the clone to that oversized, shifted box instead of the cropped
   result a visitor recognises as "the logo". Cloning the wrapper carries the
   already-correct crop (and the img's mix-blend-mode class, i.e. the same
   invert effect every other link in this chrome has) along for free.

   HIDING THE ORIGINAL

   `visibility: hidden`, not `display: none` or removal: the wrapper still
   needs to occupy its box so .video-hero__word's own layout (BUILD-PLAN
   section 5 in section-video-hero.css) does not shift. It is only ever set
   AFTER the clone is live — progressive enhancement, so a script error
   between creating the clone and this line, or this script simply not
   running, leaves the original wordmark visible exactly as it always was
   rather than gone. Tearing the clone down (below 750px, or if
   .overlay-nav__home ever disappears) restores it the same way.

   NO NAV WORDMARK BELOW 750px — THIS RUNS THERE TOO, JUST DIFFERENTLY

   component-overlay-nav.css hides .overlay-nav__home outright under
   max-width: 749px ("the toggle plus the utility already fill the row") —
   there is deliberately no nav slot on a phone for this to become. Below
   that width this script has nothing to animate TOWARDS, so it tears the fly
   clone down and lets the ORIGINAL hero wordmark show exactly as it would
   with this script absent, rather than collapsing into a hidden, zero-size
   target (a real bug an earlier version of this file had — the clone
   animated to (0, 0) because that is what a `display: none` element's rect
   is). Because a visitor can cross that breakpoint without a page load
   (rotating a tablet, resizing a desktop window), this is not a one-time
   check: `sync()` re-runs it on every debounced resize and creates or tears
   down the clone as needed, in either direction.

   PERFORMANCE

   Only `transform` is written per frame — a compositor-only property on
   this element (nothing above it in the DOM isolates that, per the P18 note
   in component-hero-wordmark-fly.css), so the browser never has to run
   layout or paint to update it, only the compositor step. There is no
   per-frame opacity write any more (nothing fades — see above). Neither
   `travel` — now potentially several hundred px taller than the old fixed
   cap — nor the removal of the phase split changes that cost: it is still
   one multiply-add per axis, once per frame. getBoundingClientRect() — the
   one genuinely expensive call here — never runs inside the scroll path,
   only from measure() (on load, and once more on the window `load` event
   for any late font/image layout shift) and from sync() on a debounced
   resize. The scroll listener is passive and does the actual style write
   inside requestAnimationFrame, so it is throttled to the display's own
   refresh rate no matter how many scroll events the browser dispatches;
   `lastP` short-circuits that rAF callback back out immediately once
   progress stops changing (already 0 at the top of a long page, or already
   1 once scrolled past `travel`), so idle scrolling elsewhere on the page
   costs one comparison, not a style write. Below the 750px breakpoint the
   clone does not exist at all, so onScroll's guard clause is the only cost
   scrolling a phone ever pays.
   ========================================================================== */
(function () {
  'use strict';

  function whenReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  whenReady(function () {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Scoped to .video-hero__word, NOT a bare '.video-hero__mark-crop' —
    // THE DEFAULT WORDMARK (snippets/header-overlay-nav.liquid) reuses that
    // same class on its own fallback <span>, which lives in the header and
    // therefore renders BEFORE the hero section in document order. An
    // unscoped querySelector grabbed that fallback span instead of the real
    // hero mark, which cloned and animated the wrong element and broke the
    // whole scroll effect on the homepage. .video-hero__word only ever
    // wraps the real hero wordmark (sections/video-hero.liquid) — the
    // header's fallback is never inside it — so this is unambiguous no
    // matter what else on the page happens to share the crop class.
    var heroCrop = document.querySelector('.video-hero__word .video-hero__mark-crop');
    var navHome = document.querySelector('.overlay-nav__home');
    if (!heroCrop || !navHome) return;

    // The homepage-only fallback (snippets/header-overlay-nav.liquid, "THE
    // DEFAULT WORDMARK") is what every OTHER page relies on for a logo in
    // this slot, since none of them have a hero to animate one in from —
    // so on the homepage specifically it has to step aside for exactly as
    // long as this script is the one occupying that slot, the same way
    // heroCrop itself does. Optional: an older theme editor draft of this
    // header without the setting configured yet would have none.
    var navHomeMark = navHome.querySelector('.overlay-nav__home-mark');

    var fly = null;
    var finalDeltaX = 0;
    var finalDeltaY = 0;
    var scale = 1;
    var travel = 240;
    var lastP = -1;
    var ticking = false;
    var heroBlend = 'difference';
    var landed = false;

    // SCROLL-DRIVEN WHERE THE BROWSER CAN DO IT ITSELF. A fixed element
    // repositioned from a scroll listener is always one frame behind the
    // native scroll, and on iOS that lag reads as a stutter against the
    // content moving under it. Browsers with CSS scroll-driven animations
    // (Safari 26+, Chrome 115+) get the same translate/scale as a keyframe
    // animation on a scroll(root) timeline (component-hero-wordmark-fly.css,
    // .hero-wordmark-fly--scroll-driven): the compositor moves it, in lockstep
    // with the scroll, and the JS below only measures and flips the landed
    // state. Everywhere else the JS still writes the transform per frame.
    var useScrollTimeline = !!(window.CSS && CSS.supports && CSS.supports('animation-timeline', 'scroll()'));

    // NOT offsetParent: it is null for `display: none`, which is what this
    // needs to detect, but per spec it is ALSO null for any `position: fixed`
    // element regardless of visibility — and .overlay-nav__home is fixed, so
    // that check reported "hidden" even above 750px. getComputedStyle's
    // `display` says what the breakpoint in component-overlay-nav.css
    // actually toggles, with no such false positive.
    function navHomeIsVisible() {
      return getComputedStyle(navHome).display !== 'none';
    }

    function createFly() {
      fly = heroCrop.cloneNode(true);
      fly.classList.add('hero-wordmark-fly');
      fly.setAttribute('aria-hidden', 'true');
      fly.style.position = 'fixed';

      // THE INVERT EFFECT LIVES ON THIS WRAPPER, NOT THE CLONED CHILD.
      // `will-change` on this wrapper (removed from component-hero-wordmark-
      // fly.css) was one isolating ancestor property; `transform` is
      // another, and that one cannot be removed — this whole element exists
      // to be transformed every frame. So the blend cannot live on the
      // cloned child: it has to be on THIS element, the one actually
      // carrying the transform, because transform only isolates a
      // DIFFERENT element's blend, never its own.
      //
      // heroBlend is read from heroCrop's OWN (still-attached, still real)
      // img, not fly's cloned one — a node fresh out of cloneNode() is not
      // yet in the document, and getComputedStyle() on a detached node
      // cannot resolve a class-based rule at all, only inline styles; it
      // silently answers "normal" for exactly that reason, which looks like
      // no blend was ever applied. Reading it before cloning, from an
      // element that has been on the page since Liquid rendered it, has no
      // such problem. Whatever word_blend actually is — difference,
      // exclusion, screen — travels with this value rather than a
      // hardcoded "difference"; the clone's own copy is switched to normal
      // permanently so nothing ever blends twice (the wrapper is the one
      // switching between normal and heroBlend now — see applyProgress()).
      var heroImg = heroCrop.querySelector('img');
      heroBlend = heroImg ? getComputedStyle(heroImg).mixBlendMode : 'difference';
      var flyImg = fly.querySelector('img');
      if (flyImg) flyImg.style.mixBlendMode = 'normal';

      // By request: plain white while still in transit (the giant hero
      // wordmark included — that is what "not yet positioned in the header"
      // covers at progress 0), inverting only once it has actually arrived.
      // landed's own state is reset here, not left over from a previous
      // create/destroy cycle (crossing the 750px breakpoint back and forth).
      landed = false;
      fly.style.mixBlendMode = 'normal';

      navHome.insertAdjacentElement('afterend', fly);
    }

    function destroyFly() {
      if (!fly) return;
      fly.parentNode.removeChild(fly);
      fly = null;
      heroCrop.style.visibility = '';
      if (navHomeMark) navHomeMark.style.visibility = '';
      lastP = -1;
    }

    function measure() {
      if (!fly) return;

      var scrollY = window.pageYOffset || document.documentElement.scrollTop;

      // heroCrop is still the real, in-flow wrapper at this point (it is
      // only ever hidden below, after this read) — its rect is therefore
      // exactly what a visitor at THIS scroll position sees right now.
      // Converting to document space (+ scrollY) makes it a stable anchor:
      // "where this box sits when scrollY is 0", independent of whatever
      // scrollY happens to be at the moment this function runs (page load
      // is not always scrollY 0 — a restored scroll position, an anchor-link
      // arrival — and this keeps the start of the animation correct either
      // way).
      var mr = heroCrop.getBoundingClientRect();
      var startRect = {
        top: mr.top + scrollY,
        left: mr.left,
        width: mr.width,
        height: mr.height
      };

      // Freeze the clone's box in absolute px. It cannot be left to resolve
      // its own width via --video-hero-word-width (section-video-hero.css):
      // that custom property is only DEFINED on the hero section's own
      // wrapper, and this clone no longer lives inside it.
      fly.style.width = startRect.width + 'px';
      fly.style.height = startRect.height + 'px';
      fly.style.top = startRect.top + 'px';
      fly.style.left = startRect.left + 'px';

      // .overlay-nav__home is `position: fixed`, so its rect is already in
      // viewport space and already scroll-invariant — nothing to convert.
      var endRect = navHome.getBoundingClientRect();

      // See THE PIVOT / THE MATH above: everything is expressed relative to
      // each box's own bottom-centre point, to match the clone's
      // transform-origin (component-hero-wordmark-fly.css).
      var startCenterX = startRect.left + startRect.width / 2;
      var startBottomY = startRect.top + startRect.height;
      var navCenterX = endRect.left + endRect.width / 2;
      var navBottomY = endRect.top + endRect.height;

      finalDeltaX = navCenterX - startCenterX;
      finalDeltaY = navBottomY - startBottomY;
      // A boost on .overlay-nav__home's own literal size (an 11px kit-label
      // box, ~25px tall with its padding), not endRect.height straight: at
      // that literal size the wordmark's thin strokes are almost all
      // anti-aliased edge, which is part of why the invert effect on it
      // reads fainter than the bold sans-serif labels beside it. Landing
      // bigger (still bottom-anchored to the same navBottomY, so it still
      // sits in the same spot) gives the strokes enough solid interior to
      // help, without touching .overlay-nav__home's own literal size, which
      // still governs its focus ring and click target. 1.3, not the earlier
      // 1.7 — still enough boost to keep the strokes legible, reduced
      // because 1.7 read as too large landed next to the rest of the chrome.
      //
      // Desktop only — re-checked here, not cached, so crossing 750px on a
      // resize (not just a page load) picks the right one. Below 750px
      // component-overlay-nav.css already sizes .overlay-nav__home down
      // (its own "374px" note) to fit the gap between the toggle and the
      // Book Now button, a budget tight enough that it measured out to a
      // 1px margin at the narrowest common phone width — boosting the
      // landed size there the same way would eat straight back into that
      // margin and reopen the collision that rule exists to avoid.
      // Desktop used to land at 1.3x the link box; brought back to 1x on
      // 7 Sep 2026 so the landed mark sits smaller in the chrome row with air
      // above and below it, inside the paper fade (custom.css, body::before).
      var sizeBoost = 1;
      scale = startRect.height > 0 ? (endRect.height * sizeBoost) / startRect.height : 1;

      // See WHY ONE FORMULA IS BOTH: this is what makes scroll-tracking and
      // "arrive at the target" the same interpolation, so there is no
      // separate slide phase and therefore no seam between phases. A tiny
      // floor guards only against a same-position pathological case
      // (finalDeltaY == 0), not a real layout.
      travel = Math.max(1, -finalDeltaY);

      if (useScrollTimeline) {
        fly.style.setProperty('--hwf-dx', finalDeltaX + 'px');
        fly.style.setProperty('--hwf-dy', finalDeltaY + 'px');
        fly.style.setProperty('--hwf-scale', String(scale));
        fly.style.setProperty('--hwf-travel', travel + 'px');
        fly.style.transform = '';
        fly.classList.add('hero-wordmark-fly--scroll-driven');
      }

      heroCrop.style.visibility = 'hidden';
      if (navHomeMark) navHomeMark.style.visibility = 'hidden';

      lastP = -1; // force the next applyProgress() to write, even if p is unchanged
      applyProgress(currentProgress());
    }

    function currentProgress() {
      var scrollY = window.pageYOffset || document.documentElement.scrollTop;
      var p = scrollY / travel;
      if (p < 0) return 0;
      if (p > 1) return 1;
      return p;
    }

    function applyProgress(p) {
      if (!fly || p === lastP) return;
      lastP = p;

      if (!useScrollTimeline) {
        var tx = finalDeltaX * p;
        var ty = finalDeltaY * p;
        var s = 1 + (scale - 1) * p;
        fly.style.transform = 'translate(' + tx + 'px, ' + ty + 'px) scale(' + s + ')';
      }

      // Plain white everywhere except the exact instant it has fully
      // arrived in the header — see createFly() for why this switch lives
      // here rather than as a permanent value. Guarded on the `landed`
      // flag, not written unconditionally every frame: mix-blend-mode is
      // cheap on its own, but there is no reason to touch style at all on
      // the ~99% of frames where p hasn't crossed 1 yet.
      var isLanded = p >= 1;
      if (isLanded !== landed) {
        landed = isLanded;
        fly.style.mixBlendMode = landed ? heroBlend : 'normal';
        // Lets CSS react to "the hero has scrolled away" (the mobile nav
        // fade in custom.css) without a second scroll listener.
        document.documentElement.classList.toggle('hero-wordmark-landed', landed);
      }
    }

    function onScroll() {
      if (!fly || ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        applyProgress(currentProgress());
        ticking = false;
      });
    }

    // The single entry point for "does the clone need to exist right now",
    // called on load and on every debounced resize. Both directions matter:
    // a visitor can resize past 750px in either direction without a page
    // load (see NO NAV WORDMARK BELOW 750px above).
    function sync() {
      if (navHomeIsVisible()) {
        if (!fly) createFly();
        measure();
      } else {
        destroyFly();
      }
    }

    var resizeTimer = null;
    function onResize() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(sync, 150);
    }

    sync();
    window.addEventListener('load', sync, { once: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
  });
})();
